import { useEffect, useState } from "react";
import { TrendingUp } from "lucide-react";
import { Input } from "@/components/ui/input";
import { TargetBar, TargetEditor } from "@/components/leads/SalesTargets";
import { api } from "@/lib/api";
import { inr0, todayIST } from "@/lib/format";

const Tile = ({ label, value, testid, children }) => (
  <div className="rounded-2xl border border-[#E7E2D6] bg-white p-4" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="mt-1 font-display text-2xl font-semibold text-[#0F382C]">{value}</div>
    {children}
  </div>
);

const RepTable = ({ reps, month, onSaved }) => (
  <div className="overflow-x-auto rounded-2xl border border-[#E7E2D6] bg-white" data-testid="earnings-table">
    <table className="w-full text-sm">
      <thead><tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground"><th className="p-3">Salesperson</th><th className="p-3">Converted vs target</th><th className="p-3">Order value vs target</th><th className="p-3 text-right">Orders</th><th className="p-3">Set target</th></tr></thead>
      <tbody>{reps.map((r) => (
        <tr key={r.id} className="border-t border-[#F1EDE3] align-middle" data-testid={`earnings-row-${r.id}`}>
          <td className="p-3 font-medium">{r.name}<div className="text-xs font-normal text-muted-foreground">{r.buyers} of {r.customers} customers ordering</div></td>
          <td className="min-w-[160px] p-3"><TargetBar done={r.converted} target={r.target_converted} testid={`target-converted-${r.id}`} /></td>
          <td className="min-w-[180px] p-3"><TargetBar done={r.value} target={r.target_value} money testid={`target-value-${r.id}`} /></td>
          <td className="p-3 text-right font-mono">{r.orders}</td>
          <td className="p-3"><TargetEditor rep={r} month={month} onSaved={onSaved} /></td>
        </tr>))}
      </tbody>
    </table>
    {reps.length === 0 && <div className="p-4 text-xs text-muted-foreground" data-testid="earnings-empty">No salespeople yet</div>}
  </div>
);

export const SalesEarnings = ({ admin }) => {
  const [month, setMonth] = useState(todayIST().slice(0, 7));
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => { if (month) api.get("/sales/earnings", { params: { month } }).then((r) => setData(r.data)).catch(() => setData(null)); }, [month, tick]);
  const t = data?.total;
  return (
    <div className="mb-6 space-y-3" data-testid="sales-earnings">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 font-display text-lg font-semibold"><TrendingUp className="h-4 w-4 text-[#D96B43]" />{admin ? "Sales team this month" : "My month"}</div>
          <div className="text-xs text-muted-foreground">Converted = leads that became members this month. Order value = confirmed orders this month from all customers you converted.</div>
        </div>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-40" data-testid="earnings-month" />
      </div>
      {t && <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile label="Leads converted" value={t.converted} testid="earnings-converted"><TargetBar done={t.converted} target={t.target_converted} compact testid="earnings-converted-bar" /></Tile>
        <Tile label="Customers ordering" value={t.buyers} testid="earnings-buyers" />
        <Tile label="Orders" value={t.orders} testid="earnings-orders" />
        <Tile label="Order value" value={inr0(t.value)} testid="earnings-value"><TargetBar done={t.value} target={t.target_value} money compact testid="earnings-value-bar" /></Tile>
      </div>}
      {admin && data && <RepTable reps={data.reps} month={month} onSaved={() => setTick((x) => x + 1)} />}
    </div>
  );
};
